import ContactForm from "../components/ContactForm";
import SiteHeader from "../components/SiteHeader";
import { ServiceVisual } from "../components/ServiceVisual";
import TrackedLink from "../components/TrackedLink";
import { customerConnectivity } from "../content/clientResults";
import "../styles/build-migrate.css";

const capabilities = [
  {
    title: "Containers & application delivery",
    description: "Choose how to run and deploy your applications, review container architecture, and improve the builds, release pipelines and environments your developers use.",
    icon: "deploy",
    id: "containers",
  },
  {
    title: "Networking & private connectivity",
    description: "Redesign connections between customer systems, clouds and datacentres. Simplify routing and access, resolve IP conflicts, and reduce the connections your team needs to maintain.",
    icon: "network",
    id: "networking",
  },
  {
    title: "Infrastructure cost & observability",
    description: "Reduce unnecessary compute, database and networking costs. Improve monitoring and tracing so your team can understand performance, investigate problems and make informed capacity decisions.",
    icon: "observability",
    id: "cost",
  },
  {
    title: "Application, database & network migrations",
    description: "Plan and implement moves to AWS, within AWS or away from it—with dependencies, data integrity, rollout stages and rollback considered before the change.",
    icon: "migrate",
    id: "migrations",
  },
] as const;

// Supporting evidence from the CV, with no client or industry identifiers.
const projects = [
  {
    title: "Production deployments",
    detail: "I rebuilt container images and release workflows while moving live workloads to ECS.",
    result: "90 → 20 min",
    resultLabel: "main production deployment time",
  },
  {
    title: "Sensor-data processing",
    detail: "I built an AWS pipeline with data engineers and data scientists to ingest, process and analyse sensor telemetry.",
    result: "~1 TB / day",
    resultLabel: "sensor data processed",
  },
] as const;

const deliverySteps = [
  { title: "Review the system", detail: "Understand the architecture, business priorities and the problem you need to solve.", output: "Findings & priorities" },
  { title: "Choose the approach", detail: "Compare trade-offs and agree the scope, validation, rollout and rollback plan.", output: "An agreed plan" },
  { title: "Implement & hand over", detail: "Make and test the changes, check the result, and document how to operate it.", output: "Working changes & handover" },
] as const;

function CapabilityIcon({ type }: { type: typeof capabilities[number]["icon"] | "build" }) {
  return (
    <svg className="bm-capability-icon" viewBox="0 0 32 32" aria-hidden="true">
      {type === "build" && <path d="m4 10 12-6 12 6-12 6-12-6Zm0 6 12 6 12-6M4 22l12 6 12-6" />}
      {type === "migrate" && <path d="M5 10h22m-6-6 6 6-6 6M27 23H5m6-6-6 6 6 6" />}
      {type === "deploy" && <><circle cx="9" cy="6" r="3" /><circle cx="9" cy="26" r="3" /><circle cx="25" cy="6" r="3" /><path d="M9 9v14m0-7h8a8 8 0 0 0 8-7" /></>}
      {type === "network" && <><rect x="11" y="3" width="10" height="8" rx="2" /><rect x="2" y="22" width="10" height="8" rx="2" /><rect x="20" y="22" width="10" height="8" rx="2" /><path d="M16 11v6M7 22v-5h18v5" /></>}
      {type === "observability" && <><rect x="3" y="4" width="26" height="22" rx="3" /><path d="M7 17h4l3-6 4 11 3-6h4M11 30h10M16 26v4" /></>}
    </svg>
  );
}

export default function PlatformServicePage() {
  return (
    <main className="service-main service-platform build-migrate-page">
      <SiteHeader />

      <section className="bm-shell bm-hero" aria-labelledby="bm-title">
        <div className="bm-hero-copy">
          <h1 id="bm-title">AWS architecture.{" "}<span>Hands-on engineering.</span></h1>
          <p className="bm-intro">
            I help founders and engineering teams design new systems and improve
            what’s already running. I work through the architecture decisions and
            implement the changes, balancing performance, running costs and the
            practical needs of your team.
          </p>
          <div className="hero-actions">
            <TrackedLink className="button button-primary" href="#contact" eventName="Contact CTA Click" eventProps={{ location: "build-migrate-hero" }}>
              Discuss your project <span aria-hidden="true">↗</span>
            </TrackedLink>
            <a className="button button-secondary" href="#project-work">See relevant work</a>
          </div>
        </div>

        <div className="bm-hero-aside" aria-hidden="true">
          <div className="bm-illustration"><ServiceVisual type="platform" /></div>
        </div>
      </section>

      <section className="bm-credential-band" aria-label="AWS certifications">
          <div className="bm-shell bm-certifications">
            <div className="bm-certification">
              <img src="/aws-certified-devops-engineer-professional.png" alt="" width="44" height="44" decoding="async" />
              <p><span>AWS Certified</span><strong>DevOps Engineer – Professional</strong></p>
            </div>
            <div className="bm-certification">
              <img src="/aws-certified-advanced-networking-specialty.png" alt="" width="44" height="44" decoding="async" />
              <p><span>AWS Certified</span><strong>Advanced Networking – Specialty</strong></p>
            </div>
            <div className="bm-certification">
              <img src="/aws-certified-sysops-administrator-associate.png" alt="" width="44" height="44" decoding="async" />
              <p><span>AWS Certified</span><strong>SysOps Administrator – Associate</strong></p>
            </div>
          </div>
      </section>

      <section className="bm-services" aria-labelledby="bm-scope-title">
        <div className="bm-shell bm-section">
        <h2 id="bm-scope-title">Where I can help.</h2>
        <article className="bm-architecture" id="architecture">
          <div className="bm-capability-title">
            <CapabilityIcon type="build" />
            <h3>Architecture &amp; technical decisions</h3>
          </div>
          <p>Design or rethink the system behind your product—from application hosting to sensor-data pipelines. Compare options against scale, cost, complexity and what your team can maintain.</p>
        </article>
        <div className="bm-capabilities">
          {capabilities.map((item) => (
            <article className="bm-capability" id={item.id} key={item.icon}>
              <div className="bm-capability-title"><CapabilityIcon type={item.icon} /><h3>{item.title}</h3></div>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
        <details className="bm-tools">
          <summary>Technical experience behind the work</summary>
          <p>ECS / Fargate, EC2, RDS / PostgreSQL, VPC, IAM, Control Tower, Terraform, Docker, GitHub Actions, GitLab CI, CloudWatch and OpenTelemetry.</p>
          <p>The tools follow the project and your existing setup—not the other way around.</p>
        </details>
        </div>
      </section>

      <section className="bm-work" id="project-work" aria-labelledby="bm-work-title">
        <div className="bm-shell bm-section">
          <h2 id="bm-work-title" className="bm-lower-heading">Results from client work.</h2>
          <article className="bm-proof-feature">
            <div className="bm-proof-story">
              <h3>Faster customer access.<br />Lower connection costs.</h3>
              <p>{customerConnectivity.change}</p>
            </div>
            <dl className="bm-proof-metrics">
              {[customerConnectivity.setupTime, customerConnectivity.monthlyCost].map((metric) => (
                <div key={metric.label}>
                  <dt>{metric.label}</dt>
                  <dd><span>{metric.before}</span><span className="bm-metric-arrow" aria-label="to">→</span><strong>{metric.after}</strong></dd>
                </div>
              ))}
            </dl>
          </article>
          <div className="bm-proof-grid">
            {projects.map((project) => (
              <article className="bm-proof-card" key={project.title}>
                <h3>{project.title}</h3>
                <div className="bm-proof-result">
                  <strong>{project.result}</strong><span>{project.resultLabel}</span>
                </div>
                <p>{project.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bm-shell bm-section bm-delivery" aria-labelledby="bm-delivery-title">
        <h2 id="bm-delivery-title" className="bm-lower-heading">How I deliver.</h2>
        <ol className="bm-delivery-list" role="list">
          {deliverySteps.map((step, index) => (
            <li key={step.title}>
              <div className="bm-delivery-title"><span aria-hidden="true">0{index + 1}</span><h3>{step.title}</h3></div>
              <p>{step.detail}</p>
              <div className="bm-delivery-output"><span>You get</span><strong>{step.output}</strong></div>
            </li>
          ))}
        </ol>
      </section>

      <section className="bm-contact-section" id="contact" aria-labelledby="bm-contact-title">
        <div className="bm-shell bm-contact-panel">
          <div className="bm-contact-copy">
            <p className="section-kicker">Let’s talk</p>
            <h2 id="bm-contact-title">What would you like to change?</h2>
            <p>Tell me what you’re building, improving or trying to understand. We can discuss whether I can help.</p>
            <div className="bm-engagement-note">
              <p><strong>Your work isn’t passed down to another subcontractor.</strong></p>
              <p>I’m available for short projects, fixed-scope work and longer-term engagements.</p>
            </div>
            <p className="bm-personal-reply">I’ll reply personally.</p>
          </div>
          <ContactForm
            variant="compact"
            contextPrompt="A few details to see if I can help."
            messageLabel="What are you working on?"
            messagePlaceholder="What would you like to build, improve or understand? Include any timing or constraints that matter."
            subjectLabel="AWS architecture and engineering enquiry"
          />
        </div>
      </section>

      <footer className="service-footer">
        <span>© 2026 Ebi Consulting Oy · Y-tunnus 3597750-8</span>
        <a href="/">Back to the main site ↑</a>
      </footer>
    </main>
  );
}
